/**
 * TokenManager — Singleton for proactive silent token renewal
 *
 * Purpose:
 *   Ensures that authentication tokens are always fresh so that recordings,
 *   AI-note generation, and every other API / WebSocket interaction can
 *   continue seamlessly for sessions lasting 30 minutes to 4+ hours.
 *
 * Key behaviours:
 *   1. Reads the real `exp` claim from the JWT to know exactly when it expires.
 *   2. Schedules a silent refresh *before* expiry using a configurable
 *      percentage-based buffer (default 20 % of token lifetime).
 *   3. Never opens a popup — uses `acquireTokenSilent` only.
 *   4. Retries up to 3 times with exponential back-off if silent refresh fails.
 *   5. Emits DOM events (`token:refreshed`, `token:refresh-failed`) so any
 *      component can react without tight coupling.
 *   6. Validates that acquired tokens are not already expired — if MSAL
 *      returns a stale cached token, forces a network refresh automatically.
 *   7. Circuit-breaker: after MAX_CONSECUTIVE_EXPIRED consecutive expired
 *      tokens, stops retrying and escalates through a 3-tier recovery:
 *        Tier 1: ssoSilent() — uses Azure AD session cookie (no UI).
 *        Tier 2: Emits `token:relogin-required` so the UI can offer
 *                acquireTokenPopup() (small popup, page stays intact).
 *        Tier 3: Emits `token:auth-failure` so the UI can force a full
 *                redirect logout/login as a last resort.
 *
 * Configuration (environment variables — set per environment):
 *   REACT_APP_TOKEN_LIFETIME_SECONDS   — fallback lifetime if `exp` claim
 *                                         cannot be read (default 3600 = 1 h)
 *   REACT_APP_TOKEN_REFRESH_BUFFER_PERCENT — how early to refresh, expressed
 *                                            as a percentage of the token
 *                                            lifetime (default 20)
 */

// ---------------------------------------------------------------------------
// Constants & safeguards
// ---------------------------------------------------------------------------

/** Minimum interval between two refresh attempts (prevents tight loops). */
var MIN_REFRESH_INTERVAL_MS = 30000; // 30 s

/** Maximum allowed buffer percentage (ensures >= 50 % of lifetime is usable). */
var MAX_BUFFER_PERCENT = 50;

/** If the token has less than this much time left and refresh fails, we
 *  surface a user-visible warning. */
var CRITICAL_THRESHOLD_MS = 60000; // 60 s

/** Maximum silent-refresh retries before giving up for this cycle. */
var MAX_RETRY_ATTEMPTS = 3;

/** Base delay for exponential back-off between retries (ms). */
var RETRY_BASE_DELAY_MS = 2000;

/**
 * Circuit-breaker: if we receive this many consecutive already-expired tokens
 * (even after forceRefresh), stop retrying and surface an auth failure.
 * This prevents the infinite 30-second loop observed when MSAL's refresh
 * token itself has expired or been revoked.
 */
var MAX_CONSECUTIVE_EXPIRED = 3;

/**
 * MSAL error codes that indicate the Azure AD session is dead and no amount
 * of silent retries will help.  When we see one of these, we skip straight
 * to the 3-tier recovery escalation instead of wasting retries.
 *
 * - interaction_required / AADSTS160021: Azure AD B2C session cookie expired.
 * - block_iframe_reload: MSAL's hidden iframe was redirected to the login
 *   page (session dead), causing a page reload inside the iframe.
 * - monitor_window_timeout: The hidden iframe timed out waiting for a
 *   response from Azure AD (session dead or network issue).
 */
var NON_RETRYABLE_ERROR_CODES = [
  'interaction_required',
  'block_iframe_reload',
  'monitor_window_timeout',
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Decode the payload of a JWT and return the parsed JSON object.
 * Returns `null` on any failure.
 */
function decodeJwtPayload(token) {
  try {
    if (!token || typeof token !== 'string') return null;
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const padded = parts[1] + '='.repeat((4 - (parts[1].length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

/**
 * Extract the expiry timestamp (ms since epoch) from a JWT `exp` claim.
 * Returns `null` if the claim is missing or unparseable.
 */
function getTokenExpiryMs(token) {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number') return null;
  return payload.exp * 1000;
}

// ---------------------------------------------------------------------------
// TokenManager class
// ---------------------------------------------------------------------------

class TokenManager {
  constructor() {
    // ---- MSAL references (set via `initialize`) ----
    this._msalInstance = null;
    this._account = null;

    // ---- Cached token state ----
    this._cachedToken = null;
    this._tokenExpiryMs = null;

    // ---- Configuration (read from env, with sensible defaults) ----
    this._configuredLifetimeMs =
      (parseInt(process.env.REACT_APP_TOKEN_LIFETIME_SECONDS, 10) || 3600) * 1000;

    const rawPercent =
      parseInt(process.env.REACT_APP_TOKEN_REFRESH_BUFFER_PERCENT, 10);
    this._refreshBufferPercent =
      Number.isFinite(rawPercent) && rawPercent > 0
        ? Math.min(rawPercent, MAX_BUFFER_PERCENT)
        : 20;

    // ---- Internal timers ----
    this._refreshTimerId = null;
    this._retryCount = 0;

    // ---- Circuit-breaker counter for consecutive expired tokens ----
    this._consecutiveExpiredCount = 0;

    // ---- State flags ----
    this._initialized = false;
    this._refreshInProgress = false;

    // ---- Shared promise for concurrent getToken() callers (Fix: Issue 1 & 2) ----
    this._refreshPromise = null;

    // ---- Visibility change handler reference (Fix: Issue 5) ----
    this._visibilityHandler = null;

    // ---- Logging prefix (no static _tag — use _ts() for timestamped logs) ----
  }

  // -----------------------------------------------------------------------
  // Timestamped logging prefix
  // -----------------------------------------------------------------------

  /** Return a timestamped log prefix for all console output (local timezone, time only). */
  _ts() {
    return `[TokenManager] | ${new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 })} |`;
  }

  // -----------------------------------------------------------------------
  // Public API
  // -----------------------------------------------------------------------

  /**
   * Bind the manager to a live MSAL instance + account and kick off the
   * proactive refresh cycle.  Safe to call more than once (e.g. on account
   * change) — previous timers are cleared.
   */
  initialize(msalInstance, account) {
    if (!msalInstance || !account) {
      console.warn(this._ts(), 'initialize() called without msalInstance or account — skipping');
      return;
    }

    // ------------------------------------------------------------------
    // Idempotency guard (Fix #2): If already initialized with the same
    // MSAL instance and a refresh is in progress, don't start a parallel
    // acquisition cycle.  This prevents the cascading re-initialization
    // problem where iframe reloads (block_iframe_reload) cause with-auth.js
    // to call initialize() repeatedly, creating 5-6 concurrent cycles.
    // ------------------------------------------------------------------
    if (this._initialized && this._msalInstance === msalInstance) {
      // Update account reference in case it changed
      this._account = account;

      if (this._refreshInProgress) {
        console.log(this._ts(), 'initialize() called while refresh in progress — skipping duplicate cycle', {
          account: account?.username || account?.name || 'unknown',
        });
        return;
      }

      // Not refreshing but already initialized — clear old timers and restart
      console.log(this._ts(), 'initialize() re-called — clearing timers and restarting cycle', {
        account: account?.username || account?.name || 'unknown',
      });
      this._clearRefreshTimer();
    }

    this._msalInstance = msalInstance;
    this._account = account;
    this._initialized = true;
    this._consecutiveExpiredCount = 0;

    console.log(this._ts(), 'Initialized', {
      configuredLifetimeSec: this._configuredLifetimeMs / 1000,
      refreshBufferPercent: this._refreshBufferPercent,
      account: account?.username || account?.name || 'unknown',
    });

    // Acquire an initial token and start the refresh cycle
    this._acquireAndSchedule();

    // Set up visibility change listener for tab sleep / device lock recovery
    this._setupVisibilityHandler();
  }

  /**
   * Return a valid id-token string.
   *
   * 1. If the cached token is still valid (with buffer), return it immediately.
   * 2. Otherwise perform a silent acquisition (with `forceRefresh` when the
   *    token is near expiry) and return the fresh token.
   * 3. Never opens a popup or redirect — callers get either a token or an
   *    error they can handle.
   *
   * @param {object} [options]
   * @param {boolean} [options.forceRefresh=false]  Force a network call to
   *   Azure AD even if the cached token looks valid.
   * @returns {Promise<string|null>}  The id-token, or `null` if acquisition
   *   fails after retries.
   */
  async getToken({ forceRefresh = false } = {}) {
    if (!this._initialized) {
      console.warn(this._ts(), 'getToken() called before initialize()');
      return null;
    }

    // Fast path — cached token is still comfortably valid
    if (!forceRefresh && this._isCachedTokenValid()) {
      // Reset circuit-breaker counter — a valid cached token proves the system is healthy
      // (Fix: Issue 7 — prevents stale counter from causing premature circuit breaker trips)
      this._consecutiveExpiredCount = 0;
      return this._cachedToken;
    }

    // Slow path — need a fresh token
    return this._acquireSilent(forceRefresh);
  }

  /**
   * Update the account reference (e.g. after a token event sets a new active
   * account).
   */
  setAccount(account) {
    if (!account) return;
    this._account = account;
  }

  /**
   * Tear down timers.  Call on logout / unmount.
   */
  destroy() {
    this._clearRefreshTimer();
    // Clean up visibility change listener (Fix: Issue 5)
    if (this._visibilityHandler) {
      document.removeEventListener('visibilitychange', this._visibilityHandler);
      this._visibilityHandler = null;
    }
    this._cachedToken = null;
    this._tokenExpiryMs = null;
    this._initialized = false;
    this._consecutiveExpiredCount = 0;
    this._refreshPromise = null;
    console.log(this._ts(), 'Destroyed');
  }

  // -----------------------------------------------------------------------
  // Diagnostics (useful for debugging / dev-tools)
  // -----------------------------------------------------------------------

  /** Return a snapshot of the current state for logging. */
  getStatus() {
    const now = Date.now();
    return {
      initialized: this._initialized,
      hasToken: !!this._cachedToken,
      tokenExpiryMs: this._tokenExpiryMs,
      tokenExpiresIn: this._tokenExpiryMs ? Math.round((this._tokenExpiryMs - now) / 1000) + 's' : null,
      configuredLifetimeSec: this._configuredLifetimeMs / 1000,
      refreshBufferPercent: this._refreshBufferPercent,
      refreshInProgress: this._refreshInProgress,
      retryCount: this._retryCount,
      consecutiveExpiredCount: this._consecutiveExpiredCount,
    };
  }

  // -----------------------------------------------------------------------
  // Internal — token acquisition
  // -----------------------------------------------------------------------

  /**
   * Acquire a token silently and, on success, cache it and schedule the next
   * proactive refresh.
   *
   * @param {boolean} [forceRefresh=false]  Force a network call to Azure AD.
   */
  async _acquireAndSchedule(forceRefresh = false) {
    const token = await this._acquireSilent(forceRefresh);
    if (token) {
      this._scheduleProactiveRefresh(token);
    }
  }

  /**
   * Core silent acquisition.  Retries up to MAX_RETRY_ATTEMPTS with
   * exponential back-off.  Returns the id-token string or `null`.
   *
   * Post-acquisition validation:
   *   After MSAL returns a token, we check the JWT `exp` claim.  If the
   *   token is already expired and we haven't yet tried `forceRefresh`,
   *   we immediately retry with `forceRefresh: true` to bypass MSAL's
   *   cache and hit the network.  If the token is *still* expired after
   *   a forced refresh, we increment the circuit-breaker counter and
   *   treat it as a failure.
   */
  async _acquireSilent(forceRefresh = false) {
    // ------------------------------------------------------------------
    // Fix: Issue 1 & 2 — Shared Promise for concurrent callers.
    // Instead of polling with _waitForOngoingRefresh (which could return
    // stale/null tokens after 15s timeout), concurrent callers now await
    // the same promise that resolves with the actual refresh result.
    // This also handles the 3-tier recovery case (up to 120s for popup)
    // without any timeout issues.
    // ------------------------------------------------------------------
    if (this._refreshInProgress && this._refreshPromise) {
      // Another call is already refreshing — await the same result
      console.log(this._ts(), 'getToken() waiting on in-progress refresh (shared promise)');
      return this._refreshPromise;
    }

    this._refreshInProgress = true;
    this._retryCount = 0;

    // Create a shared promise that concurrent callers can await.
    // The _resolveRefresh function is captured in closure and called at
    // every return point to notify all waiters of the outcome.
    let _resolveRefresh;
    this._refreshPromise = new Promise((resolve) => {
      _resolveRefresh = resolve;
    });

    try {
      while (this._retryCount <= MAX_RETRY_ATTEMPTS) {
        try {
          const request = {
            scopes: ['openid', 'profile', 'offline_access'],
            account: this._account,
            forceRefresh: forceRefresh || this._retryCount > 0,
          };

          const response = await this._msalInstance.acquireTokenSilent(request);
          const idToken = response?.idToken;

          if (!idToken) {
            throw new Error('acquireTokenSilent returned no idToken');
          }

          // ------------------------------------------------------------------
          // Post-acquisition expiry validation
          // ------------------------------------------------------------------
          const expiryMs = getTokenExpiryMs(idToken);
          const now = Date.now();

          if (expiryMs && expiryMs <= now) {
            // Token is already expired!
            const expiredBySec = Math.round((now - expiryMs) / 1000);

            if (!forceRefresh && this._retryCount === 0) {
              // First attempt was cache-only — retry with forceRefresh
              console.warn(
                this._ts(),
                `Acquired token is already expired by ${expiredBySec}s — retrying with forceRefresh: true`,
              );
              forceRefresh = true;
              // Don't increment _retryCount — this is a validation-triggered
              // retry, not an error retry.  Continue the while loop.
              continue;
            }

            // We already tried forceRefresh and still got an expired token.
            // Increment circuit-breaker counter.
            this._consecutiveExpiredCount++;
            console.error(
              this._ts(),
              `Token still expired by ${expiredBySec}s after forceRefresh ` +
              `(consecutive expired: ${this._consecutiveExpiredCount}/${MAX_CONSECUTIVE_EXPIRED})`,
            );

            if (this._consecutiveExpiredCount >= MAX_CONSECUTIVE_EXPIRED) {
              // Circuit breaker tripped — stop the refresh loop entirely.
              // Escalate through 3-tier recovery before giving up.
              console.error(
                this._ts(),
                'Circuit breaker tripped — too many consecutive expired tokens. ' +
                'Starting 3-tier recovery escalation...',
              );
              this._clearRefreshTimer();

              // Attempt 3-tier recovery (ssoSilent → popup → redirect)
              const recovered = await this._attemptTieredRecovery();
              if (recovered) {
                // Recovery succeeded — reset and resume normal operation
                _resolveRefresh(this._cachedToken);
                return this._cachedToken;
              }

              // All tiers failed — emit auth-failure for the UI to force logout
              _resolveRefresh(null);
              return null;
            }

            // Not yet at circuit-breaker limit — treat as a retryable failure
            throw new Error(
              `acquireTokenSilent returned an expired token (expired by ${expiredBySec}s)`,
            );
          }

          // Token is valid — reset circuit-breaker counter
          this._consecutiveExpiredCount = 0;

          // Cache
          this._cachedToken = idToken;
          this._tokenExpiryMs = expiryMs || (now + this._configuredLifetimeMs);
          this._retryCount = 0;

          console.log(this._ts(), 'Token acquired', {
            expiresIn: Math.round((this._tokenExpiryMs - now) / 1000) + 's',
            forceRefresh,
          });

          // Emit success event
          this._emit('token:refreshed', { expiresAt: this._tokenExpiryMs });

          _resolveRefresh(idToken);
          return idToken;
        } catch (err) {
          this._retryCount++;
          const errCode = err?.errorCode || '';
          const errMsg = err?.message || String(err);

          console.warn(this._ts(), `Silent acquisition attempt ${this._retryCount}/${MAX_RETRY_ATTEMPTS} failed:`, errMsg);

          // ----------------------------------------------------------------
          // Fix #1 + #4: Detect non-retryable MSAL errors and skip straight
          // to 3-tier recovery.  These errors mean the Azure AD session is
          // dead — no amount of silent retries will help.
          //
          // Errors detected:
          //   - interaction_required (AADSTS160021: session expired)
          //   - block_iframe_reload (iframe redirected to login page)
          //   - monitor_window_timeout (iframe timed out)
          // ----------------------------------------------------------------
          const isNonRetryable = NON_RETRYABLE_ERROR_CODES.some(
            (code) => errCode === code || errMsg.includes(code),
          );

          if (isNonRetryable) {
            console.error(
              this._ts(),
              `Non-retryable MSAL error detected: "${errCode || errMsg}" — ` +
              'skipping remaining retries, escalating to 3-tier recovery...',
            );
            this._clearRefreshTimer();

            // Attempt 3-tier recovery (ssoSilent → popup → redirect)
            const recovered = await this._attemptTieredRecovery();
            if (recovered) {
              _resolveRefresh(this._cachedToken);
              return this._cachedToken;
            }

            // All tiers failed
            _resolveRefresh(null);
            return null;
          }

          if (this._retryCount > MAX_RETRY_ATTEMPTS) {
            break;
          }

          // Exponential back-off (only for retryable errors)
          const delay = RETRY_BASE_DELAY_MS * Math.pow(2, this._retryCount - 1);
          console.log(this._ts(), `Retrying in ${delay}ms...`);
          await this._sleep(delay);
        }
      }

      // ------------------------------------------------------------------
      // All retries exhausted (Fix #3): Check if the last error was a
      // non-retryable type that somehow wasn't caught above (defensive).
      // If so, trigger 3-tier recovery instead of just emitting
      // token:refresh-failed and scheduling another retry cycle.
      // ------------------------------------------------------------------
      console.error(this._ts(), 'All silent acquisition attempts failed');

      // Emit refresh-failed for telemetry regardless of path
      this._emit('token:refresh-failed', {
        cachedTokenStillValid: this._isCachedTokenValid(),
        tokenExpiresIn: this._tokenExpiryMs ? Math.round((this._tokenExpiryMs - Date.now()) / 1000) : null,
      });

      // If we still have a cached token that hasn't expired, return it
      if (this._cachedToken && this._tokenExpiryMs && Date.now() < this._tokenExpiryMs) {
        console.warn(this._ts(), 'Returning still-valid cached token despite refresh failure');
        // Schedule another attempt soon
        this._scheduleRetryRefresh();
        _resolveRefresh(this._cachedToken);
        return this._cachedToken;
      }

      // Token is fully expired and all retries failed — escalate to 3-tier recovery
      console.error(this._ts(), 'Token expired and all retries exhausted — escalating to 3-tier recovery');
      this._clearRefreshTimer();
      const recovered = await this._attemptTieredRecovery();
      if (recovered) {
        _resolveRefresh(this._cachedToken);
        return this._cachedToken;
      }

      _resolveRefresh(null);
      return null;
    } finally {
      this._refreshInProgress = false;
      this._refreshPromise = null;
    }
  }

  // -----------------------------------------------------------------------
  // Internal — proactive refresh scheduling
  // -----------------------------------------------------------------------

  /**
   * Schedule the next silent refresh based on the token's real expiry.
   *
   * Priority:
   *   1. Use the JWT `exp` claim (most accurate).
   *   2. Fall back to the configured lifetime env var.
   *
   * The buffer percentage is applied to whichever source is used.
   *
   * If the token is already expired (timeUntilExpiry <= 0), we schedule an
   * immediate force-refresh instead of entering the normal buffer calculation
   * (which would produce negative values and clamp to MIN_REFRESH_INTERVAL_MS
   * with forceRefresh: false — the root cause of the infinite stale-token loop).
   */
  _scheduleProactiveRefresh(idToken) {
    this._clearRefreshTimer();

    const now = Date.now();
    const expiryMs = getTokenExpiryMs(idToken);

    let timeUntilExpiry;
    if (expiryMs) {
      timeUntilExpiry = expiryMs - now;
    } else {
      // Fallback: assume token was just issued
      timeUntilExpiry = this._configuredLifetimeMs;
    }

    // ------------------------------------------------------------------
    // Guard: token is already expired or about to expire imminently
    // ------------------------------------------------------------------
    if (timeUntilExpiry <= 0) {
      // ------------------------------------------------------------------
      // Fix: Issue 3 — Check circuit breaker before scheduling another
      // retry for an expired token.  If the circuit breaker has already
      // tripped, don't schedule further refresh attempts — 3-tier recovery
      // should already be in progress or completed.  This prevents up to
      // 90s of unnecessary 30s retry cycles.
      // ------------------------------------------------------------------
      if (this._consecutiveExpiredCount >= MAX_CONSECUTIVE_EXPIRED) {
        console.error(
          this._ts(),
          'Token expired and circuit breaker already tripped — not scheduling further refresh attempts',
        );
        return;
      }

      console.warn(
        this._ts(),
        `Token is already expired (by ${Math.round(-timeUntilExpiry / 1000)}s) — scheduling immediate force-refresh`,
      );
      this._refreshTimerId = setTimeout(() => {
        console.log(this._ts(), 'Proactive refresh firing (force-refresh for expired token)...');
        this._acquireAndSchedule(true);
      }, MIN_REFRESH_INTERVAL_MS);
      return;
    }

    // Calculate buffer
    const bufferMs = timeUntilExpiry * (this._refreshBufferPercent / 100);
    let refreshIn = timeUntilExpiry - bufferMs;

    // Apply minimum safeguard
    refreshIn = Math.max(refreshIn, MIN_REFRESH_INTERVAL_MS);

    console.log(this._ts(), 'Proactive refresh scheduled', {
      tokenLifetimeSec: Math.round(timeUntilExpiry / 1000),
      bufferPercent: this._refreshBufferPercent,
      bufferSec: Math.round(bufferMs / 1000),
      refreshInSec: Math.round(refreshIn / 1000),
    });

    this._refreshTimerId = setTimeout(() => {
      console.log(this._ts(), 'Proactive refresh firing...');
      this._acquireAndSchedule();
    }, refreshIn);
  }

  /**
   * After a failed refresh, schedule a retry sooner (30 s) to try again
   * before the token actually expires.
   */
  _scheduleRetryRefresh() {
    this._clearRefreshTimer();

    const retryIn = MIN_REFRESH_INTERVAL_MS; // 30 s
    console.log(this._ts(), `Scheduling retry refresh in ${retryIn / 1000}s`);

    this._refreshTimerId = setTimeout(() => {
      console.log(this._ts(), 'Retry refresh firing...');
      this._acquireAndSchedule();
    }, retryIn);
  }

  // -----------------------------------------------------------------------
  // Internal — helpers
  // -----------------------------------------------------------------------

  /** Check whether the cached token is still valid with comfortable margin. */
  _isCachedTokenValid() {
    if (!this._cachedToken || !this._tokenExpiryMs) return false;
    // Consider valid if more than CRITICAL_THRESHOLD_MS remains
    return (this._tokenExpiryMs - Date.now()) > CRITICAL_THRESHOLD_MS;
  }

  _clearRefreshTimer() {
    if (this._refreshTimerId) {
      clearTimeout(this._refreshTimerId);
      this._refreshTimerId = null;
    }
  }

  _sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // -----------------------------------------------------------------------
  // Visibility change handler (Fix: Issue 5 — Tab Sleep / Device Lock)
  // -----------------------------------------------------------------------

  /**
   * Set up a `visibilitychange` listener that proactively checks token
   * validity when the page becomes visible again (e.g. after laptop lid
   * close, device lock, or extended tab backgrounding).
   *
   * When a device sleeps, `setTimeout` timers are paused by the browser.
   * The proactive refresh timer may not fire on time, leaving the token
   * expired when the device wakes.  This handler detects the wake-up and
   * triggers an immediate refresh if needed, instead of waiting for the
   * next API call to discover the problem.
   *
   * Safe to call multiple times — removes any existing listener first.
   */
  _setupVisibilityHandler() {
    // Remove any existing listener (idempotent)
    if (this._visibilityHandler) {
      document.removeEventListener('visibilitychange', this._visibilityHandler);
    }

    this._visibilityHandler = () => {
      if (document.visibilityState !== 'visible' || !this._initialized) {
        return;
      }

      console.log(this._ts(), 'Page became visible — checking token validity');

      const now = Date.now();

      // If token is expired or within critical threshold, trigger immediate refresh
      if (!this._cachedToken || !this._tokenExpiryMs || (this._tokenExpiryMs - now) <= CRITICAL_THRESHOLD_MS) {
        console.warn(this._ts(), 'Token expired or near expiry after visibility change — triggering immediate refresh');
        this._clearRefreshTimer();
        // Skip if a refresh is already in progress (e.g. triggered by an API call)
        if (!this._refreshInProgress) {
          this._acquireAndSchedule(true);
        }
      } else {
        // Token still valid — but check if the scheduled timer is still appropriate
        // (setTimeout may have drifted during sleep)
        const timeUntilExpiry = this._tokenExpiryMs - now;
        const bufferMs = timeUntilExpiry * (this._refreshBufferPercent / 100);
        const shouldRefreshIn = timeUntilExpiry - bufferMs;

        if (shouldRefreshIn <= 0) {
          console.log(this._ts(), 'Proactive refresh window passed during sleep — refreshing now');
          this._clearRefreshTimer();
          if (!this._refreshInProgress) {
            this._acquireAndSchedule();
          }
        }
        // else: token is valid and refresh timer should still be appropriate — no action needed
      }
    };

    document.addEventListener('visibilitychange', this._visibilityHandler);
  }

  // -----------------------------------------------------------------------
  // 3-Tier Recovery Escalation
  // -----------------------------------------------------------------------

  /**
   * Attempt to recover from a completely expired token through 3 tiers:
   *
   *   Tier 1 — ssoSilent():  Uses the Azure AD session cookie to get a
   *            fresh token without any user interaction.  Works when the
   *            refresh token expired but the Azure AD browser session is
   *            still alive (the most common scenario).
   *
   *   Tier 2 — Emit `token:relogin-required`:  Signals the UI (App.js) to
   *            show a modal and attempt `acquireTokenPopup()`.  The popup
   *            opens Azure AD's login page in a small window — the main
   *            page stays intact (no data loss).  The UI resolves a promise
   *            via `resolvePopupRecovery()` when the popup succeeds or fails.
   *
   *   Tier 3 — Emit `token:auth-failure`:  Signals the UI to show a
   *            countdown modal and ultimately call `handleLogout()` which
   *            does a full redirect to Azure AD.
   *
   * @returns {Promise<boolean>}  `true` if recovery succeeded (token cached).
   */
  async _attemptTieredRecovery() {
    // ---- Tier 1: ssoSilent ----
    console.log(this._ts(), 'Tier 1: Attempting ssoSilent() recovery...');
    try {
      const ssoResponse = await this._msalInstance.ssoSilent({
        scopes: ['openid', 'profile', 'offline_access'],
        account: this._account,
      });

      const idToken = ssoResponse?.idToken;
      if (idToken) {
        const expiryMs = getTokenExpiryMs(idToken);
        const now = Date.now();

        if (expiryMs && expiryMs > now) {
          // ssoSilent returned a valid, non-expired token!
          this._cachedToken = idToken;
          this._tokenExpiryMs = expiryMs;
          this._consecutiveExpiredCount = 0;

          // Update account if ssoSilent returned one
          if (ssoResponse.account) {
            this._account = ssoResponse.account;
            this._msalInstance.setActiveAccount(ssoResponse.account);
          }

          console.log(this._ts(), 'Tier 1 SUCCESS: ssoSilent() recovered a valid token', {
            expiresIn: Math.round((expiryMs - now) / 1000) + 's',
          });

          this._emit('token:refreshed', { expiresAt: expiryMs, recoveryTier: 1 });

          // Restart the proactive refresh cycle
          this._scheduleProactiveRefresh(idToken);
          return true;
        }

        console.warn(this._ts(), 'Tier 1: ssoSilent() returned an expired token — escalating to Tier 2');
      } else {
        console.warn(this._ts(), 'Tier 1: ssoSilent() returned no idToken — escalating to Tier 2');
      }
    } catch (ssoErr) {
      console.warn(this._ts(), 'Tier 1: ssoSilent() failed:', ssoErr?.message || ssoErr);
    }

    // ---- Tier 2: Emit event for UI to attempt acquireTokenPopup ----
    console.log(this._ts(), 'Tier 2: Requesting interactive popup re-authentication...');
    try {
      const popupResult = await this._requestPopupRecovery();

      if (popupResult && popupResult.idToken) {
        const expiryMs = getTokenExpiryMs(popupResult.idToken);
        const now = Date.now();

        if (expiryMs && expiryMs > now) {
          this._cachedToken = popupResult.idToken;
          this._tokenExpiryMs = expiryMs;
          this._consecutiveExpiredCount = 0;

          if (popupResult.account) {
            this._account = popupResult.account;
            this._msalInstance.setActiveAccount(popupResult.account);
          }

          console.log(this._ts(), 'Tier 2 SUCCESS: Popup re-authentication recovered a valid token', {
            expiresIn: Math.round((expiryMs - now) / 1000) + 's',
          });

          this._emit('token:refreshed', { expiresAt: expiryMs, recoveryTier: 2 });

          // Restart the proactive refresh cycle
          this._scheduleProactiveRefresh(popupResult.idToken);
          return true;
        }

        console.warn(this._ts(), 'Tier 2: Popup returned an expired token — escalating to Tier 3');
      } else {
        console.warn(this._ts(), 'Tier 2: Popup recovery failed or was dismissed — escalating to Tier 3');
      }
    } catch (popupErr) {
      console.warn(this._ts(), 'Tier 2: Popup recovery error:', popupErr?.message || popupErr);
    }

    // ---- Tier 3: Emit auth-failure for full redirect logout ----
    console.error(this._ts(), 'Tier 3: All recovery tiers exhausted. Emitting token:auth-failure for forced re-login.');
    this._emit('token:auth-failure', {
      reason: 'all_recovery_tiers_exhausted',
      consecutiveExpiredCount: this._consecutiveExpiredCount,
    });
    return false;
  }

  /**
   * Request the UI layer (App.js) to attempt an interactive popup login.
   *
   * Emits `token:relogin-required` and returns a Promise that resolves when
   * the UI calls `tokenManager.resolvePopupRecovery(result)`.
   *
   * The promise auto-rejects after 120 seconds (popup timeout).
   *
   * @returns {Promise<{idToken: string, account: object}|null>}
   */
  _requestPopupRecovery() {
    return new Promise((resolve) => {
      // Store the resolver so the UI can call it
      this._popupRecoveryResolver = resolve;

      // Auto-timeout after 2 minutes
      this._popupRecoveryTimeout = setTimeout(() => {
        console.warn(this._ts(), 'Popup recovery timed out after 120s');
        this._popupRecoveryResolver = null;
        resolve(null);
      }, 120000);

      // Signal the UI
      this._emit('token:relogin-required', {
        reason: 'token_expired_all_silent_attempts_failed',
        consecutiveExpiredCount: this._consecutiveExpiredCount,
      });
    });
  }

  /**
   * Called by the UI (App.js) after a successful or failed popup login.
   *
   * @param {object|null} result  `{ idToken, account }` on success, or
   *   `null` if the popup was closed / failed.
   */
  resolvePopupRecovery(result) {
    if (this._popupRecoveryTimeout) {
      clearTimeout(this._popupRecoveryTimeout);
      this._popupRecoveryTimeout = null;
    }
    if (this._popupRecoveryResolver) {
      this._popupRecoveryResolver(result);
      this._popupRecoveryResolver = null;
    }
  }

  /**
   * Re-initialize after a successful external re-authentication (e.g. after
   * the UI performed a popup login).  Resets the circuit breaker, caches the
   * new token, and restarts the proactive refresh cycle.
   *
   * @param {string} idToken  The fresh id-token from the popup response.
   * @param {object} [account]  The MSAL account from the popup response.
   */
  reinitializeAfterReauth(idToken, account) {
    if (!idToken) {
      console.warn(this._ts(), 'reinitializeAfterReauth() called without idToken');
      return;
    }

    const expiryMs = getTokenExpiryMs(idToken);
    const now = Date.now();

    if (expiryMs && expiryMs <= now) {
      console.error(this._ts(), 'reinitializeAfterReauth() received an expired token — ignoring');
      return;
    }

    // Reset all state
    this._cachedToken = idToken;
    this._tokenExpiryMs = expiryMs || (now + this._configuredLifetimeMs);
    this._consecutiveExpiredCount = 0;
    this._retryCount = 0;

    if (account) {
      this._account = account;
    }

    console.log(this._ts(), 'Re-initialized after re-authentication', {
      expiresIn: Math.round((this._tokenExpiryMs - now) / 1000) + 's',
      account: account?.username || account?.name || 'unchanged',
    });

    this._emit('token:refreshed', { expiresAt: this._tokenExpiryMs, recoveryTier: 'reauth' });

    // Restart proactive refresh
    this._scheduleProactiveRefresh(idToken);
  }

  /**
   * Dispatch a CustomEvent on `window` so any component can listen.
   * Payload is in `event.detail`.
   */
  _emit(eventName, detail = {}) {
    try {
      window.dispatchEvent(new CustomEvent(eventName, { detail }));
    } catch {
      // Swallow — event dispatch should never break the manager
    }
  }
}

// ---------------------------------------------------------------------------
// Singleton export
// ---------------------------------------------------------------------------

const tokenManager = new TokenManager();

export default tokenManager;
